/**
 * @file Obfuscated photos with Waldo icons (blockers) on top of faces.
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import Photo from '../Photo';
import PhotoFaceBlock from './PhotoFaceBlock';
import { IMAGE_SIZE_CODE } from '../../../constants/image-size-codes';

class PhotoObfuscated extends Component {
  static propTypes = {
    id: PropTypes.string.isRequired,
    thumbs: PropTypes.arrayOf(
      PropTypes.shape({
        width: PropTypes.number,
        height: PropTypes.number,
        url: PropTypes.string,
      }),
    ),
    className: PropTypes.string,
    imgClassName: PropTypes.string,
    faceboxes: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number))
      .isRequired,
    children: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.node),
      PropTypes.node,
    ]),
    onMouseDown: PropTypes.func,
    onMouseUp: PropTypes.func,
    onClick: PropTypes.func,
    obfuscatorMultiplier: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.func,
    ]),
    maxThumbnailSize: PropTypes.oneOf(Object.values(IMAGE_SIZE_CODE)),
    minThumbnailSize: PropTypes.oneOf(Object.values(IMAGE_SIZE_CODE)),
    blockerMinSizeThreshold: PropTypes.number,
    blockerMinSize: PropTypes.number,
    blockerMaxSize: PropTypes.number,
  };

  static defaultProps = {
    // TODO Possibly temporary until we find a better solution which
    // allows users to zoom in to a larger image size
    minThumbnailSize: IMAGE_SIZE_CODE.LARGE,
  };

  state = {
    touched: false,
    imageLoading: true,
  };

  onImageLoaded = () => {
    this.setState(() => ({ imageLoading: false }));
  };

  onMouseDown = () => {
    const { onMouseDown } = this.props;
    this.setState(() => ({ touched: true }));

    if (onMouseDown) {
      onMouseDown();
    }
  };

  onMouseUp = () => {
    const { onMouseUp } = this.props;
    this.setState(() => ({ touched: false }));

    if (onMouseUp) {
      onMouseUp();
    }
  };

  render() {
    const { imageLoading } = this.state;
    const {
      id,
      thumbs,
      className,
      imgClassName,
      faceboxes,
      children,
      obfuscatorMultiplier,
      maxThumbnailSize,
      minThumbnailSize,
      blockerMinSizeThreshold,
      blockerMinSize,
      blockerMaxSize,
      onClick,
      ...props
    } = this.props;

    const { touched } = this.state;

    const classes = classNames({
      'photo-obfuscated': true,
      clickable: !!onClick,
      toggled: touched,
      [className]: className,
      'photo-obfuscated-loading': imageLoading,
    });

    return (
      <div className={classes}>
        <div // eslint-disable-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
          className="photo-obfuscated-target"
          onMouseDown={this.onMouseDown}
          onTouchStart={this.onMouseDown}
          onMouseUp={this.onMouseUp}
          onTouchEnd={this.onMouseUp}
          onClick={onClick}
        >
          <div className="photo-obfuscated-loading-bg" />

          <Photo
            thumbs={thumbs}
            className="photo-obfuscated-photo photo-obfuscated-front"
            imgClassName={imgClassName}
            style={{
              clipPath: touched ? `url("#clip-${id}")` : 'none',
              WebkitClipPath: touched ? `url("#clip-${id}")` : 'none',
            }}
            onImageLoaded={this.onImageLoaded}
            maxThumbnailSize={maxThumbnailSize}
            minThumbnailSize={minThumbnailSize}
            {...props}
          />

          <Photo
            thumbs={thumbs}
            className="photo-obfuscated-photo photo-obfuscated-back"
            imgClassName={imgClassName}
            maxThumbnailSize={maxThumbnailSize}
            minThumbnailSize={minThumbnailSize}
            {...props}
          />

          <PhotoFaceBlock
            id={id}
            faceboxes={faceboxes}
            obfuscatorMultiplier={obfuscatorMultiplier}
            renderSvg={touched}
            blockerMinSizeThreshold={blockerMinSizeThreshold}
            blockerMinSize={blockerMinSize}
            blockerMaxSize={blockerMaxSize}
          />
        </div>

        {children}
      </div>
    );
  }
}

export default PhotoObfuscated;
